// THE BLOG ELEMENTS, from what a page needs to what it renders.
//
// One module for both halves, and both halves are pure:
//
//   requirements(page, params)   what the three element types need loaded —
//                                a post by slug, a page of the listing, a
//                                collection — keyed by element id;
//   render*(…)                   the markup, given what was loaded.
//
// The renderer (api/code/src/render/resolve) fulfils the requirements from the
// tables and the app's preview fulfils them through GraphQL — the fetch is the
// only thing that differs, and it is the only thing that is not here. Gen-2 had
// two loaders and two renderers, one pair in the edge Lambda and one in the
// admin's preview, kept identical by hand (archive/recovered/stack-pkg/src/
// libs/site-render-load/elements and stack-env/code/site-frontend/src/
// components/editor/elements); this is the one module that replaces the four.
//
// THE TEMPLATE IS THE SITE'S. A site has three blog templates — post, list,
// collection — on its row (`site.blog.templates`, see @common/schema/site/blog),
// each `{ html, style, script }`, and every element of a kind renders through
// the one for its kind; a site that has written none renders through the
// built-ins in ./blog-templates, which are what gen-2's blog configuration
// held per site. They reach this module RESOLVED, as `data.templates`, loaded
// by the same two callers that load the posts — so a caller that never asked
// for data renders the built-ins, and the template editor previews an unsaved
// template by handing it in there.
//
// They were sections once: each element named a `sectionId` and rendered
// through that row, with the section's `values` under the payload. That put
// three fixed, well-known templates in a list about placement, and previewed
// them as sections — with none of the values below behind the markup, which is
// a template error on all three. @common/schema/site/blog has the rest of the
// argument.
//
// WHAT A TEMPLATE SEES
//
//   blog-post:            values.{postId, slug, title, abstract, image, tags,
//                         date, dateText, content, author, url}
//   blog-list:            values.{items[], page, hasPrev, hasNext, prevUrl,
//                         nextUrl, tag, empty}
//   blog-post-collection: values.{collectionId, title, description, image,
//                         items[], empty}
//
// where every item and the post itself has the same shape, `content` is the
// delta already rendered to HTML (through ./paragraph, so its braces are
// escaped for the interpolation pass the way a paragraph element's are), and
// `author` is the site's author record or null. The site's own values are
// `cxt.site.values`, as they are for every element.
import * as Template from "./template";
import * as Paragraph from "./paragraph";
import { DEFAULTS } from "./blog-templates";
import { substitute } from "@common/schema/page/route";

export const POST = "blog-post";
export const LIST = "blog-list";
export const COLLECTION = "blog-post-collection";

export const TYPES = [POST, LIST, COLLECTION];

export const isBlogType = (type) => TYPES.includes(type);

// Element type → template kind. The kind is the key on the site row and in
// `data.templates`; the type is what an element declares.
export const KIND_OF = {
  [POST]: "post",
  [LIST]: "list",
  [COLLECTION]: "collection",
};

export const kindOf = (type) => KIND_OF[type] || null;

// The template an element of `type` renders through, out of the render data —
// resolved by the caller (the API's blogOf, the app's fetchBlogData) so this
// module never sees a site row. The built-in when the data carries none, which
// is what a caller that never loaded any gets, and what a kind nobody has
// written renders through anyway.
export const templateFrom = (data, type) => {
  const kind = kindOf(type);
  const own = data && data.templates && data.templates[kind];
  const template = own && typeof own === "object" ? own : DEFAULTS[kind];

  return {
    html: String((template && template.html) || ""),
    style: String((template && template.style) || ""),
    script: String((template && template.script) || ""),
  };
};

// The built-ins, re-exported under the names the suites and the docs use.
export const DEFAULT_POST_TEMPLATE = DEFAULTS.post.html;
export const DEFAULT_LIST_TEMPLATE = DEFAULTS.list.html;
export const DEFAULT_COLLECTION_TEMPLATE = DEFAULTS.collection.html;

const DEFAULT_LIMIT = 10;
const MAX_LIMIT = 100;

const limitOf = (value) => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? Math.min(n, MAX_LIMIT) : DEFAULT_LIMIT;
};

const pageOf = (value) => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// What the page needs, one entry per blog element, in element order. A slug
// is interpolated from the route's params the way a template's `{{params.x}}`
// is, so `{{params.slug}}` on a `/blog/:slug` page is the visitor's slug and
// on a page with no such param is "" — a post nobody can find, rendered as
// not found rather than as an error.
export const requirements = (page, params = {}) => {
  const out = [];

  for (const element of (page && page.elements) || []) {
    const config = element.config || {};

    if (element.type === POST) {
      out.push({
        elementid: element.elementid,
        kind: "post",
        slug: String(Template.interpolate(config.slug || "", { params }) || "").trim(),
      });
    } else if (element.type === LIST) {
      out.push({
        elementid: element.elementid,
        kind: "list",
        page: pageOf(params.page),
        limit: limitOf(config.limit),
        tag: String(config.tag || "").trim(),
      });
    } else if (element.type === COLLECTION) {
      out.push({
        elementid: element.elementid,
        kind: "collection",
        collectionId: String(config.collectionId || "").trim(),
      });
    }
  }

  return out;
};

const EMPTY_IMAGE = { url: "", description: "" };

const findAuthor = (authors, authorId) =>
  (authorId && Array.isArray(authors)
    ? authors.find((author) => author && author.authorId === authorId)
    : null) || null;

// A post as a template sees it. `url` is where the post lives: the route
// pattern given, with this post's slug in it — the post page's own route for
// the post element, the configured `postRoute` for a listing's items.
export const postValues = (post, { authors = [], postRoute = "" } = {}) => {
  if (!post) {
    return null;
  }

  const date = post.date || "";
  const image = post.image && typeof post.image === "object" ? post.image : EMPTY_IMAGE;

  return {
    postId: post.postId || "",
    slug: post.slug || "",
    title: post.title || "",
    abstract: post.abstract || "",
    image: { url: image.url || "", description: image.description || "" },
    tags: Array.isArray(post.tags) ? post.tags : [],
    date,
    dateText: date.slice(0, 10),
    publishedAt: post.publishedAt || "",
    content: Paragraph.render(post.content),
    author: findAuthor(authors, post.authorId),
    url: postRoute ? substitute(postRoute, { slug: post.slug }) || "" : "",
  };
};

// The payload IS the values: a template reads `values.title`, and the site's
// own values are on the context as they are for every element. No `types` —
// what a template sees is fixed by its kind, not declared on it.
const through = (template, payload, context) =>
  Template.render(template, { types: [], values: payload }, context, null);

// The address of the page being composed, with the params it was matched
// with put back — a `/blog/:slug` page rendering `hello` links to itself as
// `/blog/hello`. `substitute` of a literal route is the route.
const ownRoute = (context) =>
  substitute((context && context.page && context.page.route) || "", (context && context.params) || {}) || "";

// The listing's neighbours: the page's own route pattern with `page` moved.
// Page 1 is the route without the param, which is what a `/blog/:page?`
// pattern answers for `/blog/`; a route with no `page` param has no
// neighbours to link, and the template shows none.
const pageUrl = (context, number) => {
  const route = (context && context.page && context.page.route) || "";
  const params = { ...((context && context.params) || {}), page: number > 1 ? String(number) : "" };
  const url = substitute(route, params);
  return url && route.includes(":page") ? url : "";
};

export const renderPost = (element, template, entry, context, authors) => {
  const post = postValues(entry.post, { authors, postRoute: ownRoute(context) || "" });
  return through(template, post, context);
};

export const renderList = (element, template, entry, context, authors) => {
  const config = element.config || {};
  const page = entry.page || {};
  const items = (page.items || []).map((post) =>
    postValues(post, { authors, postRoute: config.postRoute || "" })
  );

  return through(template, {
    items,
    empty: items.length === 0,
    page: page.page || 1,
    limit: page.limit || limitOf(config.limit),
    tag: page.tag || "",
    hasPrev: Boolean(page.hasPrev),
    hasNext: Boolean(page.hasNext),
    prevUrl: page.hasPrev ? pageUrl(context, (page.page || 1) - 1) : "",
    nextUrl: page.hasNext ? pageUrl(context, (page.page || 1) + 1) : "",
  }, context);
};

export const renderCollection = (element, template, entry, context, authors) => {
  const config = element.config || {};
  const collection = entry.collection || {};
  const items = (entry.items || []).map((post) =>
    postValues(post, { authors, postRoute: config.postRoute || "" })
  );
  const image = collection.image && typeof collection.image === "object" ? collection.image : EMPTY_IMAGE;

  return through(template, {
    collectionId: collection.collectionId || "",
    title: collection.title || "",
    description: collection.description || "",
    image: { url: image.url || "", description: image.description || "" },
    items,
    empty: items.length === 0,
  }, context);
};

// Which post a page is ABOUT, for `{{post.title}}` in its head: the first
// post element's, if it was found. See contextOf in ./page.
export const primaryPost = (page, data) => {
  const entries = (data && data.elements) || {};

  for (const element of (page && page.elements) || []) {
    if (element.type === POST) {
      const entry = entries[element.elementid];
      return entry && entry.post ? entry.post : null;
    }
  }

  return null;
};
